// IE 2026 09 29 / ODOO-342781: роль «только чтение» (access-readonly) — чистая логика политики фронта (без Vue / DOM / стора).
// Признак и белые списки приходят от бэка: user.readonly и user.readonly_policy в ответах login / check_sessionKey
// ({version, commands, backend_methods, widget_methods, widget_open_denied, error, text}; бэк common_modules/readonly.py). Здесь —
// «можно ли это действие / этот запрос», распознавание отказа 403 read_only, разбор тела запроса для предохранителя $.ajaxPrefilter,
// фильтр пунктов меню и фильтр слоя личных отметок Бизнес-кейсов по «эпохе» копии. Стор (mainStore.readonly_*) и компоненты вызывают
// эти функции ТОЛЬКО при роли; без роли ничего из этого не вызывается (поведение фронта не меняется). Тесты — tests/unit/readonlyPolicy.test.mjs.

export const RO_ERROR = 'read_only'
export const RO_TEXT_DENIED = 'Read-only access: changes are not saved'
export const RO_TEXT_EDIT = 'Read-only access: editing is disabled'
export const RO_BADGE = 'Read-only access'
export const RO_BADGE_TIP = 'You can browse everything; saving, running tasks and other changes are disabled'

const str = (v) => (v === null || v === undefined ? '' : String(v))
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)

// команды до гарда бэка: вход, проверка сессии, список баз — бэк их не гейтит (readonly.PRE_SESSION_COMMANDS)
const PRE_SESSION = new Set(['login', 'check_sessionKey', 'list_user_databases'])

// команды бэка с записью, которые шлёт фронт (те же имена, что DENIED_COMMANDS бэка): запасной список на случай, если политика не пришла
export const BE_WRITE_COMMANDS = new Set([
    'saveRecord', 'archive_record', 'move_record', 'Save_PIVOT_TOOL', 'fill_docDEL', 'save', 'save_multi', 'select_query',
    'user_settings_save', 'user_defaults_save', 'page_defaults_save', 'workspace_defaults_save', 'i18n_values_set',
    'toggle_favorite_database', 'resolve_debug_backend', 'toggle_debug', 'databases_admin_info', 'check_password',
])

// фронтовые команды записи (executeStoreMethod и attrs меню / панелей команд): при роли не показываются и не выполняются.
// Отдельные случаи с условием — ro_allowsAction: copyPaste (paste* / import*), set_viewParam «Edit in a grid», widget_open с new / clone
export const FE_WRITE_COMMANDS = new Set([
    'onSaveRecord__Click', 'onSaveRecords__Click', 'onCloneRecord__Click', 'onSave_PIVOT_TOOL__Click',
    'onImporttGrid__Click', 'onImportFile__Click',
    'defaults_save', 'variants_save', 'variants_saveFilters', 'variants_saveWidgets',
    'test_record_start', 'test_record_stop', 'test_replay', 'client_test_run', 'client_tests_run',
    'onOpenDebug__Click', 'cascade_debug_toggle',   // отладка бэка / каскада — админский инструмент (resolve_debug_backend, toggle_debug, cascade_debug: бэк отказывает)
    'externalLink',   // «Copy link»: бэк get_externalLink пишет строку активности (workflow.activities) — роли отказано, пункт не показываем
])

// методы виджетов и бэка с записью / запуском — запасной список на случай, если политика не пришла (дизайн разд. 2.3–2.4)
const FALLBACK_DENY_WIDGET = new Set([
    'save', 'save_cow', 'save_working', 'cow_apply', 'cow_delete', 'save_element_appearance', 'save_field_edit', 'save_segment_WHERE', 'importDS',
    'create_DS_from_list', 'reload_segment_data', 'branch_merge_import', 'approve_plans', 'DELETE_records', 'DELETE_CONNECTOR_records', 'llm_fix',
    'msg_send', 'chat_participants_update', 'upload_attachment', 'restore', 'restore_predefined', 'i18n_languages_save', 'accept_reference', 'save_as_test',
    'start', 'test', 'test_debug', 'stop', 'scheduled', 'data_tasks_start', 'data_tasks_stop', 'run_commandLine', 'rebuild_fact_cache',
    'restore_last_snapshot', 'start_workspace_schedule', 'start_global_schedule', 'stop_global_schedule', 'poll_cell_edits',
    'toggle_favorite', 'set_passed', 'set_position', 'mark_read',
])
const FALLBACK_DENY_BACKEND = new Set([
    'globalJob_new', 'record_help_save', 'menu_row_format', 'get_externalLink', 'records_deepCopy', 'page_save_variant', 'page_variant_save',
    'page_variant_default', 'page_default', 'filters_save_variant', 'filters_variant_save', 'filters_variant_default', 'page_variant_delete',
    'filters_variant_delete', 'set_default_variant', 'clear_default_variant', 'auto_recalc_pause', 'auto_recalc_resume', 'auto_recalc_run_all',
    'create_branch', 'discard_branch', 'apply_branch', 'cascade_debug', 'debug_poll', 'debug_cmd', 'start_global_schedule', 'start',
    'user_defaults_save', 'page_defaults_save', 'workspace_defaults_save', 'page_save_as_new',
])

// методы, которые бэк сейчас отказывает как «спорные» (чат, симуляция, mark_read, mv_switch, update_fieldsMapping; решает ie-demo7 после лог-прогона):
// когда политика их не разрешает, отказ на фоновых и неявных вызовах — молча (без тоста), пока действие пользователя получает тост
// личные отметки Бизнес-кейсов — кнопки формы кейса (бэк guide.command_panel: ★ toggle_favorite, «пройдено» set_passed): фронт ведёт их сам
// (executeStoreMethod → guide_mark → слой браузера guideMarksLocal), бэку при роли они не уходят (он ответил бы 403). Как ЗАПРОС метод по-прежнему
// запрещён (ro_allowsRequest / предохранители отправки) — здесь только «кнопку показывать» (ro_allowsAction / checkAccess)
export const RO_LOCAL_MARK_METHODS = new Set(['toggle_favorite', 'set_passed'])
// IE 2026 09 29 / ODOO-342781 (ревью ie-dm7 F1): + settings_form (догрузка формы настроек виджета) и docs_index (индекс поиска читалки) — фронт шлёт их сам, без действия пользователя: отказ бэка — молча
export const RO_QUIET_METHODS = new Set(['mark_read', 'get_msg', 'select_chat', 'select_logsLLM', 'sim_get_tables', 'simulate_cell_edits', 'settings_form', 'docs_index'])
// IE 2026 09 29 / ODOO-342781 (ревью ie-dm7 F1): методы-ЧТЕНИЯ виджетов, которые фронт зовёт сам. Запасной список (политики с бэка нет) — deny-список выше, они в нём не значатся;
// явный список нужен, чтобы тест ловил дрейф и чтобы будущая правка FALLBACK_DENY_WIDGET их не зацепила. С политикой бэка решает политика
export const RO_FE_READ_WIDGET_METHODS = new Set(['settings_form', 'docs_index'])

// ---------------------------------------------------------------- политика
const toSet = (v) => new Set(Array.isArray(v) ? v.map(str) : [])

// политика бэка → множества; нет / не объект / нет списков — null (работают запасные списки выше)
export function ro_normPolicy(policy) {
    if (!isObj(policy)) return null
    if (!Array.isArray(policy.commands) && !Array.isArray(policy.backend_methods) && !Array.isArray(policy.widget_methods)) return null
    return {
        version: Number(policy.version) || 0,
        commands: toSet(policy.commands),
        backend_methods: toSet(policy.backend_methods),
        widget_methods: toSet(policy.widget_methods),
        widget_open_denied: Array.isArray(policy.widget_open_denied) ? policy.widget_open_denied.map(str) : ['record._meta.createNew'],
        text: str(policy.text) || RO_TEXT_DENIED,
    }
}

export function ro_widgetMethodAllowed(policy, method) {
    const p = policy && policy.widget_methods instanceof Set ? policy : ro_normPolicy(policy)
    const m = str(method)
    return p ? p.widget_methods.has(m) : (RO_FE_READ_WIDGET_METHODS.has(m) || !FALLBACK_DENY_WIDGET.has(m))   // IE 2026 09 29 / ODOO-342781 (ревью F1): + явный список собственных чтений фронта
}

export function ro_backendMethodAllowed(policy, method) {
    const p = policy && policy.backend_methods instanceof Set ? policy : ro_normPolicy(policy)
    const m = str(method)
    return p ? p.backend_methods.has(m) : !FALLBACK_DENY_BACKEND.has(m)
}

// запрос, который уходит на бэк: зеркало гарда бэка (readonly.decide) — команда из белого списка политики, executeXxx — по методу,
// widget_open — кроме создания записи (record._meta.createNew). extra — { createNew }. Нет команды в запросе — не наш случай (бэк ответит сам)
export function ro_allowsRequest(policy, command, method = '', extra = null) {
    const cmd = str(command)
    if (!cmd) return true
    if (PRE_SESSION.has(cmd)) return true
    if (cmd.startsWith('datahub') || cmd.startsWith('odoo_intake.')) return false
    const p = policy && policy.commands instanceof Set ? policy : ro_normPolicy(policy)
    if (cmd === 'executeWidgetMethod' || cmd === 'executeWidgetMethod_inNewWidget') return ro_widgetMethodAllowed(p, method)
    if (cmd === 'executeBackendMethod') return ro_backendMethodAllowed(p, method)
    if (cmd === 'widget_open') return !(extra && extra.createNew)
    if (p) return p.commands.has(cmd)
    return !BE_WRITE_COMMANDS.has(cmd)
}

// «Edit in a grid»: кнопка / пункт выключает isReadOnly списка (set_viewParam, binding …isReadOnly, setValue / checkValue false)
export function ro_isEditInGrid(a) {
    return isObj(a) && str(a.command) === 'set_viewParam' && /(^|\.)isReadOnly$/.test(str(a.binding)) && (a.setValue === false || a.checkValue === false)
}

// widget_open, который создаёт запись: New (new + widget-record / widget-list без id записи), запись без id (item), Clone (params.clone)
export function ro_widgetOpenCreates(a) {
    if (!isObj(a)) return false
    if (isObj(a.params) && a.params.clone) return true
    const item = a.item
    if (isObj(item)) return !item.id
    if (a.new) return (a.widget === 'widget-record' || a.widget === 'widget-list')
    return false
}

// действие интерфейса (attr меню / панели команд / кнопки: command, method, …): можно ли его показывать и выполнять при роли.
// Фронтовая команда — по своим спискам, обращение к бэку (executeXxx) — по политике бэка. Прочие фронтовые команды (навигация, оформление,
// открытие виджетов, копирование в буфер) разрешены: их обращения к бэку ловят предохранители отправки
export function ro_allowsAction(policy, a) {
    if (!isObj(a)) return true
    const cmd = str(a.command)
    if (!cmd) return true
    const method = str(a.method)
    if (FE_WRITE_COMMANDS.has(cmd) || BE_WRITE_COMMANDS.has(cmd)) return false
    if (cmd.startsWith('datahub') || cmd.startsWith('odoo_intake.')) return false
    if (cmd === 'executeVueObjMethod' && method === 'i18n_titles_open') return false   // карточка «Translations…»: её Apply пишет переводы (i18n_values_set)
    if (cmd === 'copyPaste') return !/^(paste|import)/i.test(method) && str(a.method2) !== 'deepCopy'   // «Deep copy» собирает запись методом бэка records_deepCopy (политика его не разрешает)
    if (cmd === 'set_viewParam') return !ro_isEditInGrid(a)
    if (cmd === 'widget_open') return !ro_widgetOpenCreates(a)
    if (cmd === 'executeWidgetMethod' && RO_LOCAL_MARK_METHODS.has(method)) return true   // ★ / «пройдено» формы кейса — слой браузера (см. RO_LOCAL_MARK_METHODS)
    if (cmd === 'executeWidgetMethod' || cmd === 'executeWidgetMethod_inNewWidget') return ro_widgetMethodAllowed(policy, method)
    if (cmd === 'executeBackendMethod') return ro_backendMethodAllowed(policy, method)
    return true
}

// расширение store.checkAccess: true — пункт / кнопку скрыть (роль включена, у attr есть команда, и политика её не разрешает). Без роли — всегда false
export function ro_hides(user, policy, attr) {
    return !!(user && user.readonly === true && isObj(attr) && attr.command && !ro_allowsAction(policy, attr))
}

// отказ на действие с заведомо «тихим» методом: политика его не разрешает, а он фоновый / неявный (тост не показываем)
export function ro_isQuiet(command, method, flags = null) {
    const f = isObj(flags) ? flags : {}
    if (f.quiet_errors || f._isPoll || f.no_notification) return true
    return RO_QUIET_METHODS.has(str(method)) && (str(command) === 'executeWidgetMethod' || str(command) === 'executeWidgetMethod_inNewWidget')
}

// ---------------------------------------------------------------- ответ бэка и запрос
// Тело 403 read_only: { status: 'error', error: 'read_only', reason, command, method, notification: {status, text, visible} }
export function ro_denial(status, body) {
    if (Number(status) !== 403) return null
    let b = body
    if (typeof b === 'string') { try { b = JSON.parse(b) } catch (e) { b = null } }
    if (!isObj(b) || b.error !== RO_ERROR) return null
    return { reason: str(b.reason), command: str(b.command), method: str(b.method), text: str(isObj(b.notification) ? b.notification.text : '') }
}

// Тело запроса ($.ajax options.data — JSON-строка) → { command, method, createNew, quiet, flags }; не JSON-объект — null (не наш запрос: пропускаем)
export function ro_parseRequest(data) {
    if (typeof data !== 'string' || data.length < 2) return null
    const head = data.charCodeAt(0) === 0xFEFF ? data.slice(1).trimStart() : data.trimStart()
    if (head[0] !== '{') return null
    let b
    try { b = JSON.parse(head) } catch (e) { return null }
    if (!isObj(b)) return null
    const flags = { quiet_errors: !!b.quiet_errors, _isPoll: !!b._isPoll, no_notification: !!b.no_notification }
    const meta = isObj(b.widget_init) && isObj(b.widget_init.record) && isObj(b.widget_init.record._meta) ? b.widget_init.record._meta : {}
    return { command: str(b.command), method: str(b.method), createNew: !!meta.createNew, flags, quiet: ro_isQuiet(b.command, b.method, flags) }
}

// ---------------------------------------------------------------- меню
// Дерево пунктов меню (attrs — дети) без того, что политика не разрешает: allows(item) → bool (для пунктов с командой). Папка, ставшая пустой
// из-за фильтра, убирается (пустая от рождения — остаётся); разделители ('-') не стоят в начале, в конце и подряд. Исходные объекты не меняет:
// узел с изменившимися детьми — копия, прочие — те же объекты (сравнение по ссылке в панелях меню сохраняется)
export function ro_filterMenu(items, allows) {
    if (!Array.isArray(items)) return items
    const isSep = (it) => isObj(it) && str(it.title) === '-'
    const walk = (list) => {
        const out = []
        let changed = false
        for (const it of list) {
            if (!isObj(it)) { out.push(it); continue }
            if (!isSep(it) && str(it.command) && !allows(it)) { changed = true; continue }
            if (Array.isArray(it.attrs) && it.attrs.length) {
                const kids = walk(it.attrs)
                if (kids.length === 0) {
                    changed = true
                    if (str(it.command)) { const { attrs, ...leaf } = it; void attrs; out.push(leaf) }   // папка с собственной разрешённой командой — остаётся ссылкой
                    continue
                }
                if (kids !== it.attrs) { out.push({ ...it, attrs: kids }); changed = true; continue }
            }
            out.push(it)
        }
        // разделители: без ведущих, хвостовых и повторных
        const clean = []
        for (const it of out) {
            if (isSep(it) && (clean.length === 0 || isSep(clean[clean.length - 1]))) { changed = true; continue }
            clean.push(it)
        }
        while (clean.length && isSep(clean[clean.length - 1])) { clean.pop(); changed = true }
        return changed ? clean : list
    }
    return walk(items)
}

// ---------------------------------------------------------------- Бизнес-кейсы: «эпоха» копии
// Отметки роли хранятся в браузере (guideMarksLocal:<пользователь>:<база>). После сброса копии Demo отметки прежней эпохи не нужны:
// эпоха — значение настройки базы (fe.guide.local_epoch, меняется при сбросе). Пусто — не проверяем. Возвращает data или null (устарели)
export function ro_localEpochFilter(data, epoch) {
    const e = str(epoch)
    if (!e || !isObj(data)) return data
    return str(data.epoch) === e ? data : null
}
export function ro_localEpochStamp(local, epoch) {
    const e = str(epoch)
    return e && isObj(local) ? { ...local, epoch: e } : local
}

// ---------------------------------------------------------------- IE 2026 09 29 / ODOO-342781 (ревью ie-dm7 F3): синтетический отказ страховки $.ajaxPrefilter

// IE 2026 09 29 / ODOO-342781 (ревью F3): отказ страховки $.ajaxPrefilter — синтетический 403 read_only БЕЗ сети: тело и причина — как у бэка
// (common_modules/readonly.py denied_body / decide), ответ отдаёт $.ajaxTransport (mainStore.initAjaxDefaults): срабатывают error / complete / глобальный
// ajaxError вызывающего, jqXHR.status 403, jqXHR.responseJSON — это тело. Раньше xhr.abort() в префильтре обрывал запрос ДО установки колбэков jQuery
// (success / error / complete не вызывались, промис вызывающего висел)
export function ro_denialReason(command, method = '') {
    const c = str(command), m = str(method)
    if (c === 'executeWidgetMethod' || c === 'executeWidgetMethod_inNewWidget') return `widget_method:${m}`
    if (c === 'executeBackendMethod') return `backend_method:${m}`
    if (c === 'widget_open') return 'widget_open:createNew'
    return `command:${c}`
}
// req — результат ro_parseRequest; text — уже переведённый текст отказа (пусто — константа)
export function ro_denialBody(req, text = '') {
    const command = str(req && req.command), method = str(req && req.method)
    return {
        status: 'error', error: RO_ERROR, reason: ro_denialReason(command, method), command, method,
        notification: { status: 'warning', text: str(text) || RO_TEXT_DENIED, visible: true },   // без isAuthenticated: false — иначе фронт уведёт на логин
    }
}
// решение префильтра: null — пропустить (не наш запрос / политика разрешает); иначе тело отказа
export function ro_prefilterBody(policy, req, text = '') {
    if (!req || !req.command) return null
    if (ro_allowsRequest(policy, req.command, req.method, { createNew: req.createNew })) return null
    return ro_denialBody(req, text)
}
// аргументы complete(status, statusText, responses, headers) транспорта jQuery для тела отказа: текст (не json) + Content-Type — jQuery сам разберёт тело
// в responseJSON / responseText, как у настоящего ответа (и при dataType не json)
export function ro_denialResponse(body) {
    return { status: 403, statusText: 'Forbidden', responses: { text: JSON.stringify(body) }, headers: 'Content-Type: application/json\r\n' }
}

// ---------------------------------------------------------------- IE 2026 09 29 / ODOO-342781 (ревью ie-dm7 F2): роль по базе

// IE 2026 09 29 / ODOO-342781 (ревью F2): роль считается ПО ОТКРЫТОЙ БАЗЕ. Бэк кладёт readonly / readonly_policy в ответ get_startup (его шлёт setup() при каждой
// инициализации базы, в том числе после смены базы в шапке / селекторе) — из того же признака, который принуждается на бэке. Ниже — чистая часть: ответ get_startup → {on, policy}
// (нет поля — старый бэк или нет роли — off) и «применить к user» (ro_applyRole)
export function ro_startupRole(answer) {
    const on = isObj(answer) && answer.readonly === true
    return { on, policy: on && isObj(answer.readonly_policy) ? answer.readonly_policy : null }
}
// признак и политика — на user (роль включена — ставим оба, нет — снимаем оба). true — роль включилась / выключилась (для вызывающего: перечитать то, что зависит
// от роли); те же значения повторно — false. user может быть реактивным (Vue): запись того же значения реактивность не поднимает
export function ro_applyRole(user, entry) {
    if (!isObj(user)) return false
    const on = !!(entry && entry.on), policy = on && isObj(entry.policy) ? entry.policy : null
    const changed = (user.readonly === true) !== on
    if (on) {
        if (user.readonly !== true) user.readonly = true
    } else if ('readonly' in user) delete user.readonly
    if (policy) {
        if (user.readonly_policy !== policy) user.readonly_policy = policy
    } else if ('readonly_policy' in user) delete user.readonly_policy
    return changed
}

// ---------------------------------------------------------------- IE 2026 09 29 / ODOO-342781 (ревью ie-dm7 F4 + F6): команды записи executeStoreMethod

// IE 2026 09 29 / ODOO-342781 (ревью F4 + F6): команды executeStoreMethod, которые при роли отклоняются В НАЧАЛЕ — до любого изменения локального состояния
// (клон записи портил живую запись — id, «изменена», суффикс названия, — раньше, чем saveRecord отказывал). Фронтовые команды записи (FE_WRITE_COMMANDS) и
// copyPaste (по ro_allowsAction: paste* / import* / deepCopy — отказ, copy* / export* — проходят). НЕ входят: executeWidgetMethod / _inNewWidget / executeBackendMethod
// (у них свои предохранители с тихим режимом и хуком _onResponse(null)), widget_open (служебные открытия), set_viewParam, executeVueObjMethod, прочие команды
export const RO_STORE_GUARDED = new Set([...FE_WRITE_COMMANDS, 'copyPaste'])
export function ro_storeDenies(policy, action) {
    return isObj(action) && RO_STORE_GUARDED.has(str(action.command)) && !ro_allowsAction(policy, action)
}

// ---------------------------------------------------------------- // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ)
// Архивная ПМ (ночной архив: doc.branch.kind = 'archive') — только чтение В ИНТЕРФЕЙСЕ. Тот же механизм, что у роли «только чтение» (store.readonly_view / readonly_allowsAction /
// checkAccess / readonly_menu / readonly_toast), но признак — открытая модель, а не пользователь, и список уже: только запись ДАННЫХ модели (правка грида, сохранение виджета в ветку,
// применение / удаление ветки, запуск задач и пересчёта, вставка / импорт). Личные настройки, варианты, справочники вне модели — как обычно (с ревью #110599 гейты — только у виджетов данных модели, ro_isModelDataWidget ниже). Запись режет бэк (branch.write_guard);
// фронт её только показывает: бейдж «Archive · read-only», грид без редактора, без ✎ / «Remind» / «Accept» / пунктов записи. Выключатель fe.ux.pmArchiveReadOnly (store)
export const RO_ARCHIVE_KIND = 'archive'
export const RO_ARCHIVE_BADGE = 'Archive · read-only'
export const RO_ARCHIVE_BADGE_TIP = 'This is a daily archive of the process model: you can browse it and compare with it; editing, saving and running tasks are disabled'
export const RO_ARCHIVE_TEXT_EDIT = 'Archive is read-only: editing is disabled'
export const RO_ARCHIVE_TEXT_DENIED = 'Archive is read-only: changes are not saved'
const ARCHIVE_DENY_FE = new Set(['onSave_PIVOT_TOOL__Click', 'onImporttGrid__Click', 'onImportFile__Click', 'test_record_start', 'client_test_run', 'client_tests_run'])
export const RO_ARCHIVE_DENY_WIDGET = new Set([
    'save', 'save_cow', 'save_working', 'cow_apply', 'cow_delete', 'save_field_edit', 'branch_merge_import', 'approve_plans', 'accept_reference', 'importDS', 'reload_segment_data',
    'start', 'test', 'test_debug', 'scheduled', 'data_tasks_start', 'run_commandLine', 'rebuild_fact_cache', 'restore_last_snapshot', 'start_workspace_schedule',
])
export const RO_ARCHIVE_DENY_BACKEND = new Set(['apply_branch', 'discard_branch', 'auto_recalc_run_all', 'auto_recalc_pause', 'auto_recalc_resume', 'start', 'globalJob_new'])

// запись ПМ (process_model_record) — архив? doc.branch.kind (запись модели), branch.kind / kind (сжатые формы пункта меню и пробы)
export function ro_isArchivePm(rec) {
    if (!isObj(rec)) return false
    const br = isObj(rec.doc) && isObj(rec.doc.branch) ? rec.doc.branch : (isObj(rec.branch) ? rec.branch : null)
    if (br) return str(br.kind) === RO_ARCHIVE_KIND
    return str(rec.kind) === RO_ARCHIVE_KIND
}

// действие интерфейса при открытом архиве: true — показывать и выполнять (тот же разбор attr, что у ro_allowsAction роли, своё узкое множество)
export function ro_archiveAllowsAction(a) {
    if (!isObj(a)) return true
    const cmd = str(a.command)
    if (!cmd) return true
    const method = str(a.method)
    if (ARCHIVE_DENY_FE.has(cmd)) return false
    if (cmd === 'copyPaste') return !/^(paste|import)/i.test(method)
    if (cmd === 'set_viewParam') return !ro_isEditInGrid(a)
    if (cmd === 'executeWidgetMethod' || cmd === 'executeWidgetMethod_inNewWidget') return !RO_ARCHIVE_DENY_WIDGET.has(method)
    if (cmd === 'executeBackendMethod') return !RO_ARCHIVE_DENY_BACKEND.has(method)
    return true
}

// IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ) — ревью webscm #110599: «только чтение архива» — только у ДАННЫХ МОДЕЛИ, а не у всего интерфейса.
// Данные модели — виджет, чьи данные читаются из версий открытой ПМ: отчёт REPORT2, записи версии (work.records с версией), список / граф над витриной версии
// (doc.mv_name / doc.source с id версии модели), виджет с params.process_model / params.version (WIDGET.params или doc.params), строка такого виджета (widget-row).
// Остальное — формы настроек (карточка записи widget-record / widget-form / widget_FORM: настройки, справочник, стол), вкладки настроек виджета, справочники и каталоги вне версии,
// настройки пользователя — редактируются и сохраняются как обычно (запись в архиве режет только бэк, branch.write_guard). Замер dev02 27.10 (2W1_001 / qZW_001-0,
// архив 1JKR_002-br6): Баланс — widget_REPORT2, doc.params.version 1JKR_001-br6; списки «Країни / Об'єкти / Основний постачальник» — widget_LIST,
// doc.mv_name aggregates."MV::SKU-CALC::Rules-1JKR_001-br6"; запись виджета отчёта лежит в work.catalogs без версии (record.version ''); карточка записи виджета
// (widget-record записи WIDGET-REPORT2) — тот же отчёт с таблицей dataSheet (widget_class widget_REPORT2) — данные модели
export const RO_ARCHIVE_SCOPE_MODEL = 'model'   // область «модель целиком» (меню ПМ: Apply / Discard / auto-recalc)
export const RO_RECORD_VIEW_WIDGETS = new Set(['widget-record', 'widget-form', 'widget-row'])
const RO_DATA_CLASSES = new Set(['widget_REPORT2'])
const RO_DATA_SEGMENTS = new Set(['WIDGET-REPORT2'])

// id версий открытой ПМ: branch.version, оси ветки (branch.axes), карта версий модели (doc.params.versions: строки или {id | value})
export function ro_pmVersions(rec) {
    const out = []
    const add = (v) => { if (isObj(v)) v = v.id || v.value; const s = str(v).trim(); if (s.length >= 3 && !out.includes(s)) out.push(s) }
    if (!isObj(rec)) return out
    const br = isObj(rec.doc) && isObj(rec.doc.branch) ? rec.doc.branch : (isObj(rec.branch) ? rec.branch : null)
    if (br) { add(br.version); if (isObj(br.axes)) Object.values(br.axes).forEach(add) }
    const vs = isObj(rec.doc) && isObj(rec.doc.params) ? rec.doc.params.versions : null
    if (isObj(vs)) Object.values(vs).forEach(add)
    else if (Array.isArray(vs)) vs.forEach(add)
    return out
}

// строка (имя витрины / источник) упоминает версию целым словом: «…Rules-1JKR_001-br6"» — да, «1JKR_001-br60» — нет
export function ro_mentionsVersion(text, versions) {
    const s = str(text)
    if (!s || !Array.isArray(versions)) return false
    for (const v of versions) {
        if (!v) continue
        let i = s.indexOf(v)
        while (i >= 0) {
            const a = i > 0 ? s[i - 1] : '', b = s[i + v.length] || ''
            if (!/[A-Za-z0-9_]/.test(a) && !/[A-Za-z0-9_-]/.test(b)) return true
            i = s.indexOf(v, i + 1)
        }
    }
    return false
}

// карточка записи / форма / строка (виджет-карточка записи виджета несёт widget_class самого виджета — замер dev 23.10, utils/formChipIcon; dev02 27.10)
export function ro_isRecordView(W) {
    return isObj(W) && (RO_RECORD_VIEW_WIDGETS.has(str(W.widget)) || str(W.widget_class) === 'widget_FORM')
}

// запись версии модели (work.records с версией; '*' — строка списка «Record versions», не запись)
export function ro_isVersionRecord(W) {
    if (!isObj(W) || !isObj(W.record)) return false
    const rec = W.record, meta = isObj(rec._meta) ? rec._meta : {}
    const ver = str(rec.version || meta.version)
    return str(meta.collection) === 'work.records' && !!ver && ver !== '*'
}

// виджет показывает данные модели (см. шапку раздела); versions — ro_pmVersions(открытой ПМ)
export function ro_isModelDataWidget(W, versions = [], depth = 0) {
    if (W === RO_ARCHIVE_SCOPE_MODEL) return true
    if (!isObj(W) || depth > 4) return false
    const rec = isObj(W.record) ? W.record : {}
    if (ro_isVersionRecord(W)) return true   // запись версии (в т.ч. её карточка)
    if (str(W.widget) === 'widget-row') return ro_isModelDataWidget(W.parentWIDGET, versions, depth + 1)   // строка — как грид, из которого открыта
    const cls = str(W.widget_class)
    if (RO_DATA_CLASSES.has(cls) || RO_DATA_SEGMENTS.has(str(rec.segment))) return true   // отчёт (и его карточка-копия)
    // карточка / форма записи: настройки, справочник, стол (у стола doc.params.version — параметр, не данные) — не данные модели; карточка записи виджета-списка — как список
    if (ro_isRecordView(W) && cls !== 'widget_LIST') return false
    const wp = isObj(W.params) ? W.params : {}
    if (str(wp.process_model) || str(wp.version)) return true
    const doc = isObj(rec.doc) ? rec.doc : {}
    const dp = isObj(doc.params) ? doc.params : {}
    if (str(dp.process_model) || str(dp.version)) return true
    if (str(dp.collection) === 'work.records') return true
    return ro_mentionsVersion(doc.mv_name, versions) || ro_mentionsVersion(doc.source, versions)
}

// грид при открытом архиве: заперт, если виджет — данные модели и это его таблица данных (dataSheet / _doc.data) или строка / запись версии целиком.
// Вкладки настроек того же виджета (гриды doc.params / viewParams) — редактируются
export function ro_archiveLocksGrid(W, versions, isDataGrid) {
    if (!ro_isModelDataWidget(W, versions)) return false
    return !!isDataGrid || str(W.widget) === 'widget-row' || ro_isVersionRecord(W)
}

export default { ro_normPolicy, ro_allowsRequest, ro_allowsAction, ro_hides, ro_widgetMethodAllowed, ro_backendMethodAllowed, ro_isEditInGrid, ro_widgetOpenCreates, ro_isQuiet,
    ro_denial, ro_parseRequest, ro_filterMenu, ro_localEpochFilter, ro_localEpochStamp,
    ro_denialReason, ro_denialBody, ro_prefilterBody, ro_denialResponse,   // IE 2026 09 29 / ODOO-342781 (ревью F3)
    ro_startupRole, ro_applyRole,   // IE 2026 09 29 / ODOO-342781 (ревью F2)
    ro_storeDenies,   // IE 2026 09 29 / ODOO-342781 (ревью F4)
    ro_isArchivePm, ro_archiveAllowsAction,   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ)
    ro_pmVersions, ro_mentionsVersion, ro_isRecordView, ro_isVersionRecord, ro_isModelDataWidget, ro_archiveLocksGrid }   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): данные модели (ревью #110599)
